import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { ShieldCheck, ShieldX, Loader2, ExternalLink, BadgeCheck } from "lucide-react";

export default function VerifyCertificate() {
  const { id } = useParams();
  const [state, setState] = useState({ loading: true, data: null });

  useEffect(() => {
    let active = true;
    api.get(`/verify/certificate/${id}`)
      .then((r) => active && setState({ loading: false, data: r.data }))
      .catch(() => active && setState({ loading: false, data: { found: false } }));
    return () => { active = false; };
  }, [id]);

  const { loading, data } = state;
  const valid = data?.found && data?.certified;
  const kind = data?.type === "tv" ? "Télévision" : "Radio";
  const dateStr = data?.reviewed_at
    ? new Date(data.reviewed_at).toLocaleDateString("fr-FR", { day: "2-digit", month: "long", year: "numeric" })
    : null;

  return (
    <div className="max-w-lg mx-auto px-4 py-10 sm:py-16" data-testid="verify-page">
      <div className="flex items-center justify-center gap-2 mb-8">
        <BadgeCheck className="text-rdcgold" size={22} />
        <span className="font-heading font-bold tracking-tight text-lg">Vérification de certificat</span>
      </div>

      {loading ? (
        <div className="flex flex-col items-center gap-3 py-16 text-muted-foreground" data-testid="verify-loading">
          <Loader2 className="animate-spin" size={28} />
          <p className="text-sm">Vérification en cours…</p>
        </div>
      ) : valid ? (
        <div className="rounded-2xl border border-emerald-500/30 bg-emerald-500/5 p-8 text-center" data-testid="verify-valid">
          <div className="mx-auto w-16 h-16 rounded-full bg-emerald-600 flex items-center justify-center mb-4">
            <ShieldCheck className="text-white" size={34} />
          </div>
          <h1 className="font-heading font-bold text-2xl text-emerald-700 dark:text-emerald-400">Certificat authentique</h1>
          <p className="text-sm text-muted-foreground mt-1">Cette chaîne est officiellement certifiée sur le portail national des médias de la RDC.</p>

          <div className="mt-6 rounded-xl bg-card border border-border divide-y divide-border text-left">
            <Row label="Chaîne" value={data.name} testid="verify-name" />
            <Row label="Type" value={`${kind}${data.province ? " · " + data.province : ""}`} />
            {dateStr && <Row label="Certifiée le" value={dateStr} />}
            <Row label="N° du certificat" value={data.id} mono />
          </div>

          <Link
            to={`/${data.type === "tv" ? "tv" : "radio"}/${data.slug || data.id}`}
            data-testid="verify-visit-channel"
            className="mt-6 inline-flex items-center gap-2 rounded-full bg-primary text-white px-5 py-2.5 text-sm font-semibold hover:bg-rdcblue transition-colors"
          >
            Voir la chaîne <ExternalLink size={15} />
          </Link>
        </div>
      ) : (
        <div className="rounded-2xl border border-rdcred/30 bg-rdcred/5 p-8 text-center" data-testid="verify-invalid">
          <div className="mx-auto w-16 h-16 rounded-full bg-rdcred flex items-center justify-center mb-4">
            <ShieldX className="text-white" size={34} />
          </div>
          <h1 className="font-heading font-bold text-2xl text-rdcred">
            {data?.found ? "Chaîne non certifiée" : "Certificat introuvable"}
          </h1>
          <p className="text-sm text-muted-foreground mt-2">
            {data?.found
              ? `« ${data.name} » n'est pas (ou plus) certifiée sur le portail. Ce certificat n'est pas valide.`
              : "Aucun certificat ne correspond à cette référence. Il peut s'agir d'un document non authentique."}
          </p>
          <Link to="/" className="mt-6 inline-flex items-center rounded-full bg-secondary px-5 py-2.5 text-sm font-semibold hover:bg-border transition-colors">
            Retour à l'accueil
          </Link>
        </div>
      )}

      <p className="text-center text-xs text-muted-foreground mt-8">Les Chaînes Numériques de la RDC — Portail national des médias</p>
    </div>
  );
}

const Row = ({ label, value, mono, testid }) => (
  <div className="flex items-center justify-between gap-4 px-4 py-3">
    <span className="text-xs text-muted-foreground shrink-0">{label}</span>
    <span data-testid={testid} className={`text-sm font-semibold text-right ${mono ? "font-mono text-xs" : ""}`}>{value}</span>
  </div>
);
